/**
 * "Machine" on `/dynamics/investigate` — the machines Defender has seen this
 * person signed in on (`GET /fo/console/device`, contract v5).
 *
 * WHY IT IS HERE AND NOT ON THE RECORD
 * ------------------------------------
 * The record drawer's Machine line-up answers "what was this machine doing in
 * that minute?" from the browser's own telemetry. This section answers the other
 * half — "what *is* the machine?" — from Defender's `DeviceInfo`: model, vendor,
 * OS build, join type, whether Defender is onboarded on it at all, when it was
 * last seen. Two different instruments, two different questions, so they sit in
 * two different places and neither one guesses the other's answer.
 *
 * COLLAPSED BY DEFAULT, ON PURPOSE
 * --------------------------------
 * The call is investigator-gated and writes an audit row **before** Defender is
 * asked (D7), so an expanded-by-default section would log a device lookup for
 * every person anyone opened, whether or not the machine was ever part of the
 * question. Collapsed, it costs no request at all: the hook is handed
 * `undefined` for the mail until someone opens it. Once opened it does send the
 * session's reason, because that is what the audit row is for — there is no
 * "look without being logged" mode, and there should not be.
 *
 * EVERY STATE IS INFORMATION, NONE IS AN EMPTY LIST
 * ------------------------------------------------
 * `ThreatHunting.Read.All` is not consented on NOVA's Graph credential today,
 * so `access_state: "unauthorised"` is the *expected* first answer in
 * production. It renders as a named, unretryable fact — the permission, and that
 * only a tenant admin can consent it — never as an error and never as "this
 * person has no device". The three answers that would otherwise collapse into
 * one blank panel stay visibly distinct: no credential (nothing was asked),
 * refused (a permission is missing), and answered-with-nothing (Defender has no
 * row for this person in 14 days, which is a real answer about an unmanaged
 * machine).
 *
 * `ram_gb` is always `not reported` here: `DeviceInfo` has no memory column, so
 * the only measured memory figure NOVA holds is the browser's `mem_total_gb` on
 * the record. The line stays rather than being dropped, so nobody goes looking
 * for a number that does not exist. There is no compliance field for the same
 * reason — that is an Intune fact, behind a permission NOVA does not hold.
 */

import { ChevronDown, Icon, Laptop } from "@norican/icons";
import {
  Alert,
  Badge,
  Collapsible,
  DescriptionList,
  EmptyState,
  Skeleton,
  Typography,
} from "@norican/react";
import { useState } from "react";

import { type FoConsoleDevice, useFoConsoleDevice } from "@/lib/api";
import { formatRelativeTime } from "@/lib/charts/config";

import {
  apiErrorDetail,
  apiErrorStatus,
  DEVICE_EMPTY_LINE,
  DEVICE_LOOKBACK_DAYS,
  DEVICE_PERMISSION,
  DEVICE_STATE_COPY,
  deviceModelText,
  deviceRow,
  NOT_REPORTED,
} from "./console-format";
import { PanelCard } from "./panel-card";
import { formatBinDateTime } from "./status";

interface MachineSectionProps {
  /** The person under investigation, or `undefined` when none is chosen. */
  mail: string | undefined;
  /** The session's reason. Without it nothing is fetched — the backend refuses anyway. */
  reason: string | null;
}

export function MachineSection({ mail, reason }: MachineSectionProps) {
  const [open, setOpen] = useState(false);
  // Handing the hook `undefined` while collapsed is what makes this section
  // free: no mail, no query, no audit row. Opening it is the consent to be
  // logged, and the reason travels with the call.
  const facts = useFoConsoleDevice(open && mail ? mail : undefined, reason);
  const data = facts.data;
  const devices = data?.devices ?? [];
  const status = apiErrorStatus(facts.error);
  const copy = data ? DEVICE_STATE_COPY[data.access_state] : null;

  return (
    <PanelCard
      title="Machine"
      caption={`What Defender says these machines are · last ${DEVICE_LOOKBACK_DAYS} days, newest first · fetched only when opened, and audit-logged when it is`}
    >
      <Collapsible.Root open={open} onOpenChange={setOpen}>
        <Collapsible.Trigger className="text-fg inline-flex items-center gap-2 rounded text-sm font-medium hover:underline focus-visible:outline-2">
          <Icon
            icon={ChevronDown}
            size="sm"
            aria-hidden="true"
            className={
              open
                ? "rotate-180 motion-safe:transition-transform"
                : "motion-safe:transition-transform"
            }
          />
          {open ? "Hide the machines" : "Show the machines Defender has seen"}
        </Collapsible.Trigger>
        <Collapsible.Panel>
          <div className="mt-3 flex flex-col gap-3">
            {!mail && (
              <Typography variant="body-sm" tone="muted">
                No person is selected, so there is nothing to look up.
              </Typography>
            )}

            {mail && reason == null && (
              <Alert tone="warning" title="No reason for this session">
                A device lookup is a per-user view: it is logged with your name, this person and
                your reason before Defender is asked. Enter a reason above and this section loads.
              </Alert>
            )}

            {/* Request-level refusals. The device route shares the per-user
                gate, so these are the same three the timeline can answer. */}
            {facts.error && status === 403 && (
              <Alert tone="warning" title="This sign-in is not on the investigator list">
                {apiErrorDetail(facts.error) ??
                  "Per-user views are open to the investigators named in NOVA's configuration only."}
              </Alert>
            )}
            {facts.error && status === 400 && (
              <Alert
                tone="warning"
                title="The backend refused the call: reason missing or too short"
              >
                {apiErrorDetail(facts.error) ??
                  "Every per-user call carries a reason of at least four characters."}
              </Alert>
            )}
            {facts.error && status === 404 && (
              <Alert tone="neutral" title={`${mail} is not a known person`}>
                {apiErrorDetail(facts.error) ??
                  "The identity map has no enabled Entra user with this mail, so there is no account name to ask Defender about."}
              </Alert>
            )}
            {facts.error && status !== 400 && status !== 403 && status !== 404 && (
              <Alert tone="danger" title="Couldn't reach the NOVA API for this lookup">
                {status === 0
                  ? "The NOVA API did not respond. This is a NOVA problem, not a Defender one."
                  : (apiErrorDetail(facts.error) ??
                    `The NOVA API answered ${status ?? "with an error"}.`)}
              </Alert>
            )}

            {open && mail && reason != null && facts.isPending && !data && !facts.error && (
              <div aria-busy="true" className="flex flex-col gap-2">
                <Skeleton height="3rem" />
                <Skeleton height="7rem" />
              </div>
            )}

            {/* A Defender-side refusal. Rendered as the named fact it is: the
                permission, who can grant it, and the backend's own sentence
                verbatim. No retry button — retrying an un-granted permission
                writes another audit row for the same answer. */}
            {data && copy && data.access_state !== "ok" && (
              <Alert tone={copy.tone} title={copy.title}>
                <div className="flex flex-col gap-2">
                  <Typography variant="body-sm">{copy.line}</Typography>
                  <Typography variant="caption" tone="muted">
                    Defender said: {data.status_detail}
                  </Typography>
                  {data.access_state === "unauthorised" && (
                    <Typography variant="caption" tone="muted">
                      Nothing is shown as zero in the meantime: the machines behind this person are
                      unknown until {DEVICE_PERMISSION} is consented, not absent.
                    </Typography>
                  )}
                </div>
              </Alert>
            )}

            {/* An `ok` answer with no rows. The one state that must never read
                as a failure, and never as "no devices". */}
            {data && data.access_state === "ok" && devices.length === 0 && (
              <EmptyState
                size="sm"
                icon={<Icon icon={Laptop} size="lg" aria-hidden="true" />}
                title={`No Defender device for this person in the last ${DEVICE_LOOKBACK_DAYS} days`}
                description={DEVICE_EMPTY_LINE}
              />
            )}

            {devices.length > 0 && (
              <>
                <div className="flex flex-col gap-2">
                  {devices.map((device) => (
                    <DeviceCard
                      key={`${device.device_name}-${device.last_seen ?? ""}`}
                      device={device}
                    />
                  ))}
                </div>
                <Typography variant="caption" tone="muted">
                  From one fixed advanced-hunting query over <code>DeviceInfo</code> — what the
                  machine <em>is</em>, never what it did. No process, no file, no network event.
                  Memory reads &ldquo;{NOT_REPORTED}&rdquo; on every row because{" "}
                  <code>DeviceInfo</code> has no memory column; the measured figure is the
                  browser&apos;s own, on the record. Compliance and disk are Intune facts NOVA
                  cannot read, so there is nowhere here to put a guess.
                  {data?.collected_at != null && (
                    <> Query ran {formatRelativeTime(data.collected_at)}.</>
                  )}
                </Typography>
              </>
            )}
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>
    </PanelCard>
  );
}

/**
 * One machine as a compact row: the name and what it is on the heading line, the
 * age of the sighting as a badge beside it, and the static facts underneath.
 *
 * The relative age is the label the row is read by — "last seen 3 days ago" is
 * what tells an investigator whether this is the machine the person is sitting
 * at right now or one they used a fortnight ago, and an absolute timestamp alone
 * makes that a subtraction the reader has to do.
 */
function DeviceCard({ device }: { device: FoConsoleDevice }) {
  const row = deviceRow(device);
  return (
    <div className="border-border flex flex-col gap-2 rounded-md border px-3 py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <Typography variant="body-sm" as="h3" className="font-semibold">
            {row.name}
          </Typography>
          <Typography variant="caption" tone="muted">
            {deviceModelText(device)}
          </Typography>
        </div>
        <Badge tone="neutral" variant="outline" size="xs" title={row.lastSeen}>
          {row.lastSeenAt == null
            ? `Last seen ${NOT_REPORTED}`
            : `Last seen ${formatRelativeTime(row.lastSeenAt)}`}
        </Badge>
      </div>
      <DescriptionList
        columns={3}
        items={[
          { label: "OS", value: row.os },
          { label: "Type", value: row.type },
          { label: "Joined", value: row.joined },
          { label: "Defender onboarding", value: row.onboarding },
          { label: "Last seen", value: formatBinDateTime(row.lastSeenAt) },
          { label: "Installed memory", value: row.memory },
        ]}
      />
    </div>
  );
}
